import { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { Cookies } from 'react-cookie';

import { styled } from "styled-components";
import "./style.css"
import * as Module from "../../modules";

import {
    collection,
    query as fsQuery,
    where,
    orderBy,
    limit,
    getDocs,
    startAfter,
    getCountFromServer,
    documentId,
    QueryConstraint,
    QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../../firebaseClient';
import LottieLoading from "./Common/LottieLoading";
import loadingAnimation from '../../animations/loading_song.json';
import Songs from "./Songs/Songs";
import DateInput from "./Inputs/DateInput";
import Player from './Player'
import Title from "../Common/Title";
import { MOBILE_MAX_WIDTH, BREAKPOINTS } from "./styles/const_style";
import CustomBottomSheet from "./BottomSheet";
import FilterChips from "./FilterChips";

type SongDoc = {
    id: string;
    title: string;
    artist: string;
    folder: string;
    created_at?: string;
    duration?: number;
    file_hash?: string;
    mail_id?: string;
    mail_date?: string;
    original_filename?: string;
};

const Wrapper = styled.div`
    width: 100%;
    height: 100vh;
    display: flex;
    flex-direction: column;
    position: relative;
    background: var(--bg-color);
    overflow: hidden;
`;

const MainContent = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding-top: 20vh;
    padding-left: 1rem;
    padding-right: 1rem;
    max-width: 640px;
    margin: 0 auto;
    width: 100%;
    pointer-events: none;

    & > * {
        pointer-events: auto;
    }

    @media screen and (max-width: ${MOBILE_MAX_WIDTH}) {
        padding-top: 20vh;
        padding-left: 0.75rem;
        padding-right: 0.75rem;
    }

    @media screen and (max-width: ${BREAKPOINTS.MOBILE}) {
        padding-top: 25vh;
        padding-left: 0.5rem;
        padding-right: 0.5rem;
    }
`;

const PlayerArea = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
`;

const WelcomeText = styled.div`
    text-align: center;
    color: var(--text-color);
    opacity: 0.6;

    h2 {
        font-size: 1.2rem;
        font-weight: 500;
        margin-bottom: 0.5rem;
    }

    p {
        font-size: 0.85rem;
    }

    @media screen and (max-width: ${MOBILE_MAX_WIDTH}) {
        h2 {
            font-size: 1.1rem;
        }

        p {
            font-size: 0.8rem;
        }
    }
`;

const CalendarOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;

    &.hidden {
        display: none;
    }
`;

const ITEMS_PER_PAGE = 20;

const GoodRecord = () => {
    const {
        loading,
        display,
        current,
        searchLength,
        songs
    } = useSelector((state: Module.RootState) => {
        return {
            loading: state.loading.state,
            display: state.song.display,
            current: state.pagination.current,
            searchLength: state.data.searchLength,
            songs: state.data.songs
        };
    }, shallowEqual);
    const dispatch = useDispatch();
    const cookies = new Cookies();

    const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [calendarDisplay, setCalendarDisplay] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [searchType, setSearchType] = useState("artist");
    const [shouldCollapseSheet, setShouldCollapseSheet] = useState(false);
    // Firestore cursor for infinite scroll
    const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot | null>(null);

    useEffect(() => {
        if (display) {
            dispatch(Module.closePlayer());
        }
        dispatch(Module.initSongs());
        dispatch(Module.initSongInfo());
        dispatch(Module.initQuery());
        dispatch(Module.initSrc());
        dispatch(Module.initPagination(1));
        dispatch(Module.setInputType('textInput'));

        const user = cookies.get("user");
        if (!user) {
            // alert("로그인이 안되어 있습니다.");
        }
        // eslint-disable-next-line
    }, []);

    // Build Firestore query constraints for title/artist (prefix) or folder (exact)
    // Note: title/artist use range prefix search — results are alphabetically ordered.
    // Firestore requires orderBy on the range field; composite indexes needed for
    // additional ordering. Folder searches use created_at ordering with sortOrder.
    const buildConstraints = useCallback((_type: string, _query: string): QueryConstraint[] => {
        if (_type === 'folder') {
            return [
                where('folder', '==', _query),
                orderBy(documentId(), sortOrder),
            ];
        }
        return [
            where(_type, '>=', _query),
            where(_type, '<=', _query + ''),
            orderBy(_type),
        ];
    }, [sortOrder]);

    const onSearch = useCallback(async (_type: string, _query: string, _current: number = 1) => {
        if (_query === "") {
            console.error("검색값이 잘못되었습니다.");
            return;
        }

        if (_type === 'folder') setCalendarDisplay(false);
        dispatch(Module.setSearchQuery({type: _type, query: _query}));
        dispatch(Module.startLoading());

        try {
            const constraints = buildConstraints(_type, _query);
            const songsCol = collection(db, 'songs');

            const countSnap = await getCountFromServer(fsQuery(songsCol, ...constraints));
            const totalCount = countSnap.data().count;

            dispatch(Module.setLength(totalCount));
            dispatch(Module.setCurrentPage(1));
            dispatch(Module.setMaxPage(Math.ceil(totalCount / ITEMS_PER_PAGE)));

            const snap = await getDocs(fsQuery(songsCol, ...constraints, limit(ITEMS_PER_PAGE)));
            const songsData = snap.docs.map(d => ({ id: d.id, ...d.data() } as SongDoc));

            setLastDoc(snap.docs[snap.docs.length - 1] ?? null);
            dispatch(Module.setSongsData(songsData));
            setHasMore(songsData.length >= ITEMS_PER_PAGE && totalCount > ITEMS_PER_PAGE);

        } catch (error) {
            console.error(error);
        } finally {
            dispatch(Module.endLoading());
            dispatch(Module.setInitial());
        }
    }, [sortOrder, buildConstraints, dispatch]);

    const handleLoadMore = useCallback(async () => {
        if (isLoadingMore || !hasMore || !lastDoc || !searchValue) return;

        setIsLoadingMore(true);
        try {
            const constraints = buildConstraints(searchType, searchValue);
            const snap = await getDocs(
                fsQuery(collection(db, 'songs'), ...constraints, startAfter(lastDoc), limit(ITEMS_PER_PAGE))
            );
            const moreData = snap.docs.map(d => ({ id: d.id, ...d.data() } as SongDoc));

            setLastDoc(snap.docs[snap.docs.length - 1] ?? null);
            dispatch(Module.appendSongsData(moreData));
            dispatch(Module.setCurrentPage(current + 1));
            setHasMore(moreData.length >= ITEMS_PER_PAGE && (current + 1) * ITEMS_PER_PAGE < searchLength);

        } catch (error) {
            console.error(error);
        } finally {
            setIsLoadingMore(false);
        }
    }, [isLoadingMore, hasMore, lastDoc, buildConstraints, searchType, searchValue, current, searchLength, dispatch]);

    const toggleCalendar = () => {
        if (calendarDisplay) {
            setCalendarDisplay(false);
            document.body.classList.remove('stop-scrolling');
        } else {
            setCalendarDisplay(true);
            document.body.classList.add('stop-scrolling');
        }
    };

    const switchSearchType = () => {
        if (searchType === "title") {
            setSearchType("artist");
        } else {
            setSearchType("title");
        }
    };

    const handleSearch = () => {
        if (searchValue.trim()) {
            onSearch(searchType, searchValue, 1);
        }
    };

    const toggleFavorites = useCallback(() => {
        setShowFavoritesOnly(!showFavoritesOnly);
    }, [showFavoritesOnly]);

    const toggleSortOrder = useCallback(() => {
        setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
        // Re-search with new sort order if there's an active search
        if (searchValue) {
            onSearch(searchType, searchValue, 1);
        }
    }, [sortOrder, searchValue, searchType, onSearch]);

    const handleSongSelect = useCallback(() => {
        setShouldCollapseSheet(true);
    }, []);

    const handleCollapseComplete = useCallback(() => {
        setShouldCollapseSheet(false);
    }, []);

    return (
        <Wrapper>
            <Title desc='Good Record' />
            {loading && <LottieLoading loadingAnimation={loadingAnimation} />}

            {/* Main Content with Player */}
            <MainContent>
                <PlayerArea>
                    {display ? (
                        <Player />
                    ) : (
                        <WelcomeText>
                            <h2>Good Record</h2>
                            <p>아래에서 곡을 검색하고 선택하세요</p>
                        </WelcomeText>
                    )}
                </PlayerArea>
            </MainContent>

            {/* Calendar Overlay */}
            <CalendarOverlay className={calendarDisplay ? '' : 'hidden'}>
                <DateInput
                    onSearch={onSearch}
                    display={calendarDisplay}
                    hideCalendar={toggleCalendar}
                />
            </CalendarOverlay>

            {/* Bottom Sheet with integrated search */}
            <CustomBottomSheet
                songCount={songs.length}
                totalCount={searchLength}
                searchValue={searchValue}
                searchType={searchType}
                onSearchValueChange={setSearchValue}
                onSearchTypeToggle={switchSearchType}
                onSearch={handleSearch}
                onCalendarToggle={toggleCalendar}
                shouldCollapse={shouldCollapseSheet}
                onCollapseComplete={handleCollapseComplete}
            >
                <FilterChips
                    onSearch={onSearch}
                    onToggleCalendar={toggleCalendar}
                    showFavoritesOnly={showFavoritesOnly}
                    onToggleFavorites={toggleFavorites}
                    sortOrder={sortOrder}
                    onToggleSortOrder={toggleSortOrder}
                />
                <Songs
                    onSearch={onSearch}
                    onLoadMore={handleLoadMore}
                    hasMore={hasMore}
                    isLoadingMore={isLoadingMore}
                    showFavoritesOnly={showFavoritesOnly}
                    onSongSelect={handleSongSelect}
                />
            </CustomBottomSheet>
        </Wrapper>
    );
};

export default GoodRecord;
